import { create } from 'zustand';
import { settingsAPI } from '../services/api';
import { notify } from './useNotificationStore';

// Types
export interface Settings {
  default_mode: string;
  default_timeout: number;
  judge_model: string;
  standalone_chat_retention: number;
  project_chat_retention: number;
  engine_response_retention: number;
  cost_warning_threshold: number | null;
  cost_hard_limit: number | null;
}

export interface APIKeyStatus {
  provider: string;
  configured: boolean;
  last_validated: string | null;
}

export interface Engine {
  name: string;
  configured: boolean;
  default_model: string;
  available_models: string[];
}

interface SettingsState {
  settings: Settings | null;
  apiKeys: APIKeyStatus[];
  engines: Engine[];
  isLoading: boolean;
  isSaving: boolean;
  loadError: boolean;

  fetchSettings: () => Promise<void>;
  updateSettings: (updates: Partial<Settings>) => void;
  saveSettings: () => Promise<void>;
  saveAPIKey: (provider: string, key: string) => Promise<void>;
  testAPIKey: (provider: string, key: string) => Promise<{ success: boolean; error?: string }>;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null) {
    const axiosError = error as { response?: { data?: { detail?: string } }; message?: string };
    if (axiosError.response?.data?.detail) {
      return axiosError.response.data.detail;
    }
    if (axiosError.message) {
      return axiosError.message;
    }
  }
  return 'An unexpected error occurred';
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  settings: null,
  apiKeys: [],
  engines: [],
  isLoading: true,
  isSaving: false,
  loadError: false,

  fetchSettings: async () => {
    set({ isLoading: true, loadError: false });
    try {
      const [settingsRes, keysRes, enginesRes] = await Promise.all([
        settingsAPI.get(),
        settingsAPI.listAPIKeys(),
        settingsAPI.listEngines(),
      ]);

      set({
        settings: settingsRes.data,
        apiKeys: keysRes.data.api_keys,
        engines: enginesRes.data.engines,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
      notify.error('Failed to load settings', getErrorMessage(error));
      set({ isLoading: false, loadError: true });
    }
  },

  updateSettings: (updates) => {
    set((state) => ({
      settings: state.settings ? { ...state.settings, ...updates } : null,
    }));
  },

  saveSettings: async () => {
    const { settings } = get();
    if (!settings) return;

    set({ isSaving: true });
    try {
      await settingsAPI.update(settings);
      notify.success('Settings saved', 'Your settings have been updated successfully.');
    } catch (error) {
      console.error('Failed to save settings:', error);
      notify.error('Failed to save settings', getErrorMessage(error));
    } finally {
      set({ isSaving: false });
    }
  },

  saveAPIKey: async (provider, key) => {
    try {
      await settingsAPI.saveAPIKey(provider, key);
      notify.success('API key saved', `Your ${provider} API key has been saved.`);
      await get().fetchSettings();
    } catch (error) {
      console.error('Failed to save API key:', error);
      notify.error('Failed to save API key', getErrorMessage(error));
      throw error;
    }
  },

  testAPIKey: async (provider, key) => {
    try {
      const response = await settingsAPI.testAPIKey(provider, key);
      const data = response.data;

      if (typeof data?.success !== 'boolean') {
        throw new Error('Invalid response from server');
      }

      return { success: data.success, error: data.error };
    } catch (error: unknown) {
      return { success: false, error: getErrorMessage(error) };
    }
  },
}));
